import type { AppUiLocale, AppUiNamespace } from './appUiLocale'
import enCommon from './locales/en/common.json'
import enDashboard from './locales/en/dashboard.json'
import enCustomerPortal from './locales/en/customerPortal.json'

export interface AppUiMessageTree {
  [key: string]: string | AppUiMessageTree
}

type LocaleBundles = Record<AppUiNamespace, AppUiMessageTree>

/** English is always available for fallback; other locales load on demand. */
export const appUiMessages: Partial<Record<AppUiLocale, LocaleBundles>> = {
  en: {
    common: enCommon as AppUiMessageTree,
    dashboard: enDashboard as AppUiMessageTree,
    customerPortal: enCustomerPortal as AppUiMessageTree,
  },
}

const localeLoaders: Record<Exclude<AppUiLocale, 'en'>, () => Promise<LocaleBundles>> = {
  'zh-Hant': async () => {
    const [common, dashboard, customerPortal] = await Promise.all([
      import('./locales/zh-Hant/common.json'),
      import('./locales/zh-Hant/dashboard.json'),
      import('./locales/zh-Hant/customerPortal.json'),
    ])
    return {
      common: common.default as AppUiMessageTree,
      dashboard: dashboard.default as AppUiMessageTree,
      customerPortal: customerPortal.default as AppUiMessageTree,
    }
  },
  'zh-CN': async () => {
    const [common, dashboard, customerPortal] = await Promise.all([
      import('./locales/zh-CN/common.json'),
      import('./locales/zh-CN/dashboard.json'),
      import('./locales/zh-CN/customerPortal.json'),
    ])
    return {
      common: common.default as AppUiMessageTree,
      dashboard: dashboard.default as AppUiMessageTree,
      customerPortal: customerPortal.default as AppUiMessageTree,
    }
  },
}

const localeLoadPromises = new Map<AppUiLocale, Promise<void>>()

/** Ensure a UI locale’s message trees are loaded (no-op for English). */
export function ensureAppUiLocaleLoaded(locale: AppUiLocale): Promise<void> {
  if (locale === 'en' || appUiMessages[locale]) {
    return Promise.resolve()
  }
  let pending = localeLoadPromises.get(locale)
  if (!pending) {
    pending = localeLoaders[locale]()
      .then((bundles) => {
        appUiMessages[locale] = bundles
      })
      .catch((err) => {
        localeLoadPromises.delete(locale)
        throw err
      })
    localeLoadPromises.set(locale, pending)
  }
  return pending
}

export function isAppUiLocaleLoaded(locale: AppUiLocale): boolean {
  return Boolean(appUiMessages[locale])
}

function getNested(tree: AppUiMessageTree | undefined, key: string): string | undefined {
  if (!tree) return undefined
  const parts = key.split('.')
  let cur: string | AppUiMessageTree | undefined = tree
  for (const part of parts) {
    if (cur == null || typeof cur === 'string') return undefined
    cur = cur[part]
  }
  return typeof cur === 'string' ? cur : undefined
}

export type TranslateParams = Record<string, string | number>

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const v = params[name]
    return v === undefined || v === null ? '' : String(v)
  })
}

/** Resolve UI string with English fallback across namespace then common. */
export function translateAppUi(
  locale: AppUiLocale,
  namespace: AppUiNamespace,
  key: string,
  params?: TranslateParams,
): string {
  const tryLocales: AppUiLocale[] = locale === 'en' ? ['en'] : [locale, 'en']
  const tryNamespaces: AppUiNamespace[] =
    namespace === 'common' ? ['common'] : [namespace, 'common']

  for (const loc of tryLocales) {
    for (const ns of tryNamespaces) {
      const tree = appUiMessages[loc]?.[ns]
      let raw: string | undefined
      if (params && params.count !== undefined) {
        const n = Number(params.count)
        if (Number.isFinite(n)) {
          const pluralSuffix = n === 1 ? '_one' : '_other'
          raw = getNested(tree, `${key}${pluralSuffix}`)
          if (!raw) raw = getNested(tree, `${key}_plural`)
        }
      }
      if (!raw) raw = getNested(tree, key)
      if (raw) return interpolate(raw, params)
    }
  }
  return key
}
